import React, { useContext, useEffect, useState } from "react";
import MaterialTable, { Column } from "material-table";
import {
    TextField,
    Select,
    MenuItem,
    IconButton,
    Button,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    styled,
    alpha,
    InputLabel,
    TextareaAutosize,
    Chip
} from "@material-ui/core";
import { Edit as EditIcon, Visibility, VisibilityOff } from "@material-ui/icons";
import { green, pink } from "@mui/material/colors";
import { IonItem } from "@ionic/react";
import * as ApiService from '../../services/ApiService';
import { ApplicationContext, ProviderContext } from "../../ApplicationState";
import moment from "moment";
var _ = require("lodash");


interface ReportData {
    guid?: string;
    id: number;
    reportName: string;
    type: string;
    frequency: string;
    password?: string;
    isActive: boolean;
    isSend: boolean; // Tracks if the report has been dispatched via the dialog
    tableData?: { id: number };
    hangfireJobId?: string;
    email?: string;
    lastUpdatedBy?: string;
    lastUpdatedOn?: string;
    createdOn?: String;
    originalSenderUserId?: string;
    recipientUserId?: String;
    providerId?: string;
}

// Added the isSend property initialized to false
const defaultReports: ReportData[] = [
    { id: 1, reportName: "Measurement Reading Report", type: '', frequency: "", password: "", isActive: false, isSend: false },
    // { id: 2, reportName: "Measurement Reading Report 2", type: '', frequency: "", password: "", isActive: false, isSend: false },
    // { id: 3, reportName: "Measurement Reading Report 3", type: '', frequency: "", password: "", isActive: false, isSend: false },
];

const GreenSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
        color: green[600],
        '&:hover': {
            backgroundColor: alpha(green[600], theme.palette.action.hoverOpacity),
        },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: green[600],
    },
}));

export default function ReportTable({ userEmail, recipientUserId }) {
    const { providerState } = useContext(ProviderContext);

    const { userState } = useContext(ApplicationContext);

    // const [data, setData] = useState<ReportData[]>(() => {
    //     const savedData = localStorage.getItem("savedReportSettings");
    //     return savedData ? JSON.parse(savedData) : defaultReports;
    // });

    const [data, setData] = useState<ReportData[]>(defaultReports);

    const [pendingChanges, setPendingChanges] = useState<{
        [tableRowId: number]: { frequency?: string; password?: string, type?: string; };
    }>({});

    const [editingRows, setEditingRows] = useState<{ [tableRowId: number]: boolean }>({});
    const [revealedPasswords, setRevealedPasswords] = useState<{ [tableRowId: number]: boolean }>({});

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [warningDialogOpen, setwarningDialogOpen] = useState(false);

    const [activeDialogRow, setActiveDialogRow] = useState<ReportData | null>(null);
    const [activeDialogTableId, setActiveDialogTableId] = useState<number | null>(null);

    // Gmail-style recipient states
    // const [emailList, setEmailList] = useState<string[]>([userEmail]);
    const [emailList, setEmailList] = useState<string[]>([userEmail]);

    const [emailInput, setEmailInput] = useState("");
    const [emailError, setEmailError] = useState("");

    const [isLoading, setisLoading] = useState(false)

    useEffect(() => {
        getReportConfiguraton()
    }, [providerState.providerId])

    const getReportConfiguraton = () => {

        try {
            ApiService.GetReportScheduleEmailReport(
                userState.authenticatedUser,
                recipientUserId,
                providerState.providerId,
            ).then(result => {

                if (result && result.length > 0) {
                    // Merge the saved records from the database directly into your clean UI rows
                    setData((prevData) =>
                        prevData.map((row) => {
                            const matchedSavedRecord = result.find((dbRow: any) => dbRow.reportId === row.id);
                            if (matchedSavedRecord) {
                                return {
                                    ...row,
                                    guid: matchedSavedRecord.id,
                                    frequency: matchedSavedRecord.frequency,
                                    type: matchedSavedRecord.format || matchedSavedRecord.type,
                                    // The password will be returned masked or encrypted from the server
                                    password: matchedSavedRecord.password,
                                    isActive: matchedSavedRecord.isActive,
                                    hangfireJobId: matchedSavedRecord.hangfireJobId, // Crucial tracking anchor parameter
                                    email: matchedSavedRecord.email,
                                    lastUpdatedBy: matchedSavedRecord.lastUpdatedBy,
                                    recipientUserId: matchedSavedRecord.recipientUserId,
                                    providerId: matchedSavedRecord.providerId,
                                    originalSenderUserId: matchedSavedRecord.originalSenderUserId,
                                    lastUpdatedOn: matchedSavedRecord.lastUpdatedOn,
                                    createdOn: matchedSavedRecord.createdOn
                                };
                            }
                            return row;
                        })
                    );
                }

            })

        } catch (ex) {
            alert('Something went wrong. Please try again')
        }
    }

    const handlePendingChange = (tableRowId: number, field: "frequency" | "password" | "type", value: string) => {
        setPendingChanges((prev) => ({
            ...prev,
            [tableRowId]: { ...prev[tableRowId], [field]: value },
        }));
    };

    const handleEditClick = (tableRowId: number, rowData: ReportData) => {
        setEditingRows((prev) => ({ ...prev, [tableRowId]: true }));
        setPendingChanges((prev) => ({
            ...prev,
            [tableRowId]: { frequency: rowData.frequency, password: rowData.password, type: rowData.type },
        }));
    };

    const handleCancelClick = (tableRowId: number) => {
        setEditingRows((prev) => ({ ...prev, [tableRowId]: false }));
        setPendingChanges((prev) => {
            const updated = { ...prev };
            delete updated[tableRowId];
            return updated;
        });
    };

    const handleSaveRow = (tableRowId: number) => {
        setisLoading(true)
        const rowChanges = pendingChanges[tableRowId];
        if (!rowChanges) return;

        const currentLiveRow = data[tableRowId];
        // 1. Calculate the target updated values
        const nextFrequency = rowChanges.frequency !== undefined ? rowChanges.frequency : currentLiveRow.frequency;
        const nextPassword = rowChanges.password !== undefined ? rowChanges.password : currentLiveRow.password;
        const nextType = rowChanges.type !== undefined ? rowChanges.type : currentLiveRow.type;

        const configMissing = !nextFrequency || !nextPassword || !nextType;

        // If configuration fields are missing, force the switch state to stay OFF
        const nextIsActiveStatus = configMissing ? false : currentLiveRow.isActive;




        // ========================================================================
        // CONDITION CHECK: If toggle is OFF (false), save locally and exit immediately.
        // This bypasses the network API completely when the schedule is inactive.
        // ========================================================================
        // if (!nextIsActiveStatus) {
        //     setData((prevData) =>
        //         prevData.map((row, index) =>
        //             index === tableRowId
        //                 ? {
        //                     ...row,
        //                     frequency: nextFrequency,
        //                     password: nextPassword,
        //                     type: nextType,
        //                     isActive: false // Force inactive state
        //                 }
        //                 : row
        //         )
        //     );

        //     // Clean up active edit states locally
        //     setEditingRows((prev) => ({ ...prev, [tableRowId]: false }));
        //     setPendingChanges((prev) => {
        //         const updated = { ...prev };
        //         delete updated[tableRowId];
        //         return updated;
        //     });

        //     console.log("Local row changes saved. API skipped because toggle is in the OFF position.");
        //     return; // Exit the entire function loop early
        // }

        if (_.isEmpty(currentLiveRow.guid) && _.isEmpty(currentLiveRow.frequency) && _.isEmpty(currentLiveRow.type) && _.isEmpty(currentLiveRow.password)) {
            const payload = {
                reportId: currentLiveRow.id,
                reportName: currentLiveRow.reportName,
                providerId: providerState.providerId,
                format: nextType,
                frequency: nextFrequency,
                password: nextPassword || "", // Pulls the password set inside this row configuration
                email: userEmail, // Passes your custom Gmail-style chip token array directly,
                createdOn: new Date(),
                lastUpdatedOn: new Date(),
                lastUpdatedBy: userState.authenticatedUser.username,
                OriginalSenderUserId: userState.authenticatedUser.username,
                RecipientUserId: recipientUserId
            };

            try {
                ApiService.AddReportConfiguration(
                    userState.authenticatedUser,
                    providerState.providerId,
                    payload).then(response => {

                        setisLoading(false)
                        if (response && response?.success === true) {

                            setData((prevData) =>
                                prevData.map((row, index) =>
                                    index === tableRowId
                                        ? {
                                            ...row,
                                            guid: response.response.id,
                                            id: response.response.reportId,
                                            reportName: response.response.reportName,
                                            frequency: response.response.frequency,
                                            type: response.response.format,
                                            // The password will be returned masked or encrypted from the server
                                            password: response.response.password,
                                            isActive: response.response.isActive,
                                            hangfireJobId: response.response.hangfireJobId, // Crucial tracking anchor parameter
                                            email: response.response.email,
                                            lastUpdatedBy: response.response.lastUpdatedBy,
                                            recipientUserId: response.response.recipientUserId,
                                            providerId: response.response.providerId,
                                            originalSenderUserId: response.response.originalSenderUserId,
                                            lastUpdatedOn: response.response.lastUpdatedOn,
                                            createdOn: response.response.createdOn
                                        }
                                        : row
                                )
                            );

                            setEditingRows((prev) => ({ ...prev, [tableRowId]: false }));
                            setPendingChanges((prev) => {
                                const updated = { ...prev };
                                delete updated[tableRowId];
                                return updated;
                            });
                        } else {
                            setisLoading(false)
                        }
                    })
            } catch (ex) {
                setisLoading(false)
                alert('Something went wrong. Please try again.')
            }


        } else {
            const updatePayload = {
                id: currentLiveRow.guid,
                reportId: currentLiveRow.id,
                reportName: currentLiveRow.reportName,
                providerId: providerState.providerId,
                format: nextType,
                frequency: nextFrequency,
                password: nextPassword,
                isActive: currentLiveRow.isActive, // needed to call hangfire only if true
                hangfireJobId: currentLiveRow.hangfireJobId || "",
                LastUpdatedBy: userState.authenticatedUser.username,
                lastUpdatedOn: new Date(),
                email: currentLiveRow.email || userEmail,
                originalSenderUserId: currentLiveRow.originalSenderUserId,
                recipientUserId: currentLiveRow.recipientUserId
            };


            const selectedStartDate = new Date()
            const selectedEndDate = new Date()
            const dateFormatString = "YYYY-MM-DD";

            try {

                ApiService.UpdateReportScheduleEmailReport(
                    userState.authenticatedUser,
                    providerState.providerId,
                    updatePayload,
                    null,
                    null,
                    'readingOn',
                    0,
                    null,
                    null
                ).then(response => {
                    setisLoading(false)
                    if (response && response.success === true) {
                        setData((prevData) =>
                            prevData.map((row, index) =>
                                index === tableRowId
                                    ? {
                                        ...row,
                                        guid: response.response.id,
                                        id: response.response.reportId,
                                        reportName: response.response.reportName,
                                        frequency: response.response.frequency,
                                        type: response.response.format,
                                        // The password will be returned masked or encrypted from the server
                                        password: response.response.password,
                                        isActive: response.response.isActive,
                                        hangfireJobId: response.response.hangfireJobId, // Crucial tracking anchor parameter
                                        email: response.response.email,
                                        lastUpdatedBy: response.response.lastUpdatedBy,
                                        recipientUserId: response.response.recipientUserId,
                                        providerId: response.response.providerId,
                                        originalSenderUserId: response.response.originalSenderUserId,
                                        lastUpdatedOn: response.response.lastUpdatedOn,
                                        createdOn: response.response.createdOn
                                    }
                                    : row
                            )
                        );
                        setEditingRows((prev) => ({ ...prev, [tableRowId]: false }));
                        setPendingChanges((prev) => {
                            const updated = { ...prev };
                            delete updated[tableRowId];
                            return updated;
                        });
                        setisLoading(false)
                    } else {
                        setisLoading(false)
                        throw new Error(response.message || "Failed to update active configuration on server.");
                    }
                })
            } catch (error) {
                setisLoading(false)
                alert(`Could not save row updates: ${error.message}`);
            }

        }


        // const updatedData = data.map((row, index) => {
        //     if (index === tableRowId) {
        //         const nextFrequency = rowChanges.frequency !== undefined ? rowChanges.frequency : row.frequency;
        //         const nextPassword = rowChanges.password !== undefined ? rowChanges.password : row.password;
        //         const nextType = rowChanges.type !== undefined ? rowChanges.type : row.type;

        //         // Auto-turn OFF toggle if user clears configuration during an edit
        //         const configMissing = !nextFrequency || !nextPassword || !nextType;

        //         return {
        //             ...row,
        //             frequency: nextFrequency,
        //             password: nextPassword,
        //             type: nextType,
        //             isActive: configMissing ? false : row.isActive
        //         };
        //     }
        //     return row;
        // });

        // saveDataAndPersist(updatedData);
        // setEditingRows((prev) => ({ ...prev, [tableRowId]: false }));
        // setPendingChanges((prev) => {
        //     const updated = { ...prev };
        //     delete updated[tableRowId];
        //     return updated;
        // });
    };

    const saveDataAndPersist = (updatedDataset: ReportData[]) => {
        setData(updatedDataset);
        localStorage.setItem("savedReportSettings", JSON.stringify(updatedDataset));
    };

    const togglePasswordReveal = (tableRowId: number) => {
        setRevealedPasswords((prev) => ({ ...prev, [tableRowId]: !prev[tableRowId] }));
    };

    const handleToggleActive = (tableRowId: number, checked: boolean, rowData: ReportData) => {
        // const updatedData = data.map((row, index) =>
        //     index === tableRowId ? { ...row, isActive: checked } : row
        // );
        // saveDataAndPersist(updatedData);

        // 1. Visually slide the switch toggle in the view layer
        setData((prevData) =>
            prevData.map((row, index) => (index === tableRowId ? { ...row, isActive: checked } : row))
        );

        // 2. Extract the absolute latest data snapshot straight out of active state memory
        // This guarantees that any server-returned tokens are captured safely
        const currentLiveRowData = data[tableRowId];

        setActiveDialogRow(currentLiveRowData);
        setActiveDialogTableId(tableRowId);


        if (checked) {
            setIsDialogOpen(true);
        } else {
            setwarningDialogOpen(true);
        }
    };

    const deleteReportSchedule = () => {

        setisLoading(true)
        if (activeDialogTableId === null || !activeDialogRow) return;

        const updatePayload = {
            id: activeDialogRow.guid,
            reportId: activeDialogRow.id,
            providerId: providerState.providerId,
            LastUpdatedBy: userState.authenticatedUser.username,
            lastUpdatedOn: new Date(),
        };


        try {
            ApiService.deleteEmailReportSchedule(
                updatePayload,
                userState.authenticatedUser,
                userState.authenticatedUser.username
            ).then((response) => {
                setisLoading(false)
                if (response && response.success === true) {
                    setData((prevData) =>
                        prevData.map((row, index) =>
                            index === activeDialogTableId
                                ? { ...row, isActive: false, isSend: false, hangfireJobId: "" }
                                : row
                        )
                    );
                } else {
                    setisLoading(false)
                    throw new Error(response.message || "Failed to deactivate active configuration on server.");
                }
            })
        } catch (error) {
            setisLoading(false)
            alert(`Could not cancel reporting. Please try again`);

            // ERROR RECOVERY: If the server call drops, snap the switch view toggle back to green (ON)
            setData((prevData) =>
                prevData.map((row, index) => (index === activeDialogTableId ? { ...row, isActive: true } : row))
            );
        } finally {
            // setisLoading(false)
            // Securely close the warning dialog and clear context row state tracking frames
            setwarningDialogOpen(false);
            setActiveDialogRow(null);
            setActiveDialogTableId(null);
        }
    }

    //  REVERT VISUAL SWITCH TOGGLE IF USER CLICKS CANCEL
    const handleCancelDeactivation = () => {

        if (activeDialogTableId !== null) {
            // Explicitly force the switch view toggle back to green (true) inside the dataset
            setData((prevData) =>
                prevData.map((row, index) => (index === activeDialogTableId ? { ...row, isActive: true } : row))
            );
        }
        setwarningDialogOpen(false);
        setActiveDialogRow(null);
        setActiveDialogTableId(null);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setActiveDialogRow(null);
        setActiveDialogTableId(null);
    };

    const closeSendDialog = () => {
        if (activeDialogTableId !== null) {
            setData((prevData) =>
                prevData.map((row, index) =>
                    index === activeDialogTableId ? { ...row, isActive: false } : row
                )
            );
        }
        handleCloseDialog()
    }

    // Flip isSend to true inside the persistent data mapping structure
    const handleSendReport = () => {
        setisLoading(true)
        if (activeDialogTableId !== null && activeDialogRow) {

            // Safety verification check: Make sure they hit save first so a database record exists
            if (!activeDialogRow.guid) {
                alert("Please save your row configuration modifications by clicking the checkmark/save action icon first!");
                handleCloseDialog();
                return;
            }

            // Package up the exact configuration values to pass to the backend API DTO model
            const payload = {
                id: activeDialogRow.guid,
                reportId: activeDialogRow.id,
                reportName: activeDialogRow.reportName,
                providerId: providerState.providerId,
                format: activeDialogRow.type,
                frequency: activeDialogRow.frequency,
                password: activeDialogRow.password || "", // Pulls the password set inside this row configuration
                email: userEmail, // Passes your custom Gmail-style chip token array directly,
                createdOn: new Date(), //email schedule send date
                lastUpdatedOn: new Date(),
                lastUpdatedBy: userState.authenticatedUser.username,
                OriginalSenderUserId: userState.authenticatedUser.username,
                RecipientUserId: recipientUserId
            };

            const selectedStartDate = '2025-07-20'
            const selectedEndDate = '2026-07-20'
            const dateFormatString = "YYYY-MM-DD";

            try {
                ApiService.scheduleGroupEmailReporting(
                    payload,
                    userState.authenticatedUser,
                    providerState.providerId,
                    null,
                    null,
                    'readingOn',
                    0,
                    null,
                    null
                ).then((response) => {
                    setisLoading(false)
                    if (response && response.success === true) {
                        // const updatedData = data.map((row, index) =>
                        //     index === activeDialogTableId ? { ...row, isSend: true } : row
                        // );
                        // saveDataAndPersist(updatedData);

                        setData((prevData) =>
                            prevData.map((row, index) =>
                                index === activeDialogTableId
                                    ? {
                                        ...row,
                                        isActive: true,
                                        // Store the server's tracking job ID straight into state memory
                                        hangfireJobId: response.jobId,
                                        // Keep any backend auto-generated database Primary Key reference if returned
                                        databaseRecordId: response.id || row.id
                                    }
                                    : row
                            )
                        );

                        handleCloseDialog();
                    } else {
                        setisLoading(false)
                        handleCloseDialog();
                        throw new Error(response?.message || "Failed to schedule background worker job execution frame.")

                    }
                })
            } catch (error) {
                setisLoading(false)
                alert(`Error scheduling report: ${error.message}`);

                // const revertedData = data.map((row, index) =>
                //     index === activeDialogTableId ? { ...row, isActive: false } : row
                // );
                // saveDataAndPersist(revertedData);

                // Roll back switch visualization to false immediately on failure
                setData((prevData) =>
                    prevData.map((row, index) => (index === activeDialogTableId ? { ...row, isActive: false } : row))
                );
                handleCloseDialog();
            }
        }

    };

    const columns: Column<ReportData>[] = [
        { title: "Report Name", field: "reportName", editable: "never" },
        {
            title: "Type",
            field: "type",
            render: (rowData) => {
                const tableRowId = rowData.tableData!.id;
                const isEditing = !!editingRows[tableRowId];
                const currentValue = isEditing
                    ? (pendingChanges[tableRowId]?.type ?? "")
                    : (rowData.type || "Not Set");

                if (!isEditing) return <span>{currentValue}</span>;

                return (
                    <Select
                        value={pendingChanges[tableRowId]?.type ?? ""}
                        displayEmpty
                        onChange={(e) => handlePendingChange(tableRowId, "type", e.target.value as string)}
                        style={{ minWidth: "120px" }}
                    >
                        <MenuItem value="">Select Type</MenuItem>
                        <MenuItem value="PDF">PDF</MenuItem>
                        <MenuItem value="XLS">XLS</MenuItem>
                    </Select>
                );
            },
        },
        {
            title: "Frequency",
            field: "frequency",
            render: (rowData) => {
                const tableRowId = rowData.tableData!.id;
                const isEditing = !!editingRows[tableRowId];
                const currentValue = isEditing
                    ? (pendingChanges[tableRowId]?.frequency ?? "")
                    : (rowData.frequency || "Not Set");

                if (!isEditing) return <span>{currentValue}</span>;

                return (
                    <Select
                        value={pendingChanges[tableRowId]?.frequency ?? ""}
                        displayEmpty
                        onChange={(e) => handlePendingChange(tableRowId, "frequency", e.target.value as string)}
                        style={{ minWidth: "120px" }}
                    >
                        <MenuItem value="">Select Frequency</MenuItem>
                        {/* <MenuItem value="Test">Test (5 Min interval)</MenuItem>
                        <MenuItem value="TestW">TestW</MenuItem>
                        <MenuItem value="TestM">TestM</MenuItem> */}
                        <MenuItem value="Daily">Daily</MenuItem>
                        <MenuItem value="Weekly">Weekly</MenuItem>
                        <MenuItem value="Monthly">Monthly</MenuItem>
                    </Select>
                );
            },
        },
        {
            title: "Set Password",
            field: "password",
            render: (rowData) => {
                const tableRowId = rowData.tableData!.id;
                const isEditing = !!editingRows[tableRowId];
                const isRevealed = !!revealedPasswords[tableRowId];

                if (!isEditing) {
                    const hasPassword = !!rowData.password;
                    return (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span>
                                {hasPassword ? (isRevealed ? rowData.password : "••••••••") : "Not Set"}
                            </span>
                            {hasPassword && (
                                <IconButton size="small" onClick={() => togglePasswordReveal(tableRowId)} style={{ marginLeft: "8px" }}>
                                    {isRevealed ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                            )}
                        </div>
                    );
                }

                return (
                    <TextField
                        type="text"
                        value={pendingChanges[tableRowId]?.password ?? ""}
                        placeholder="Enter password"
                        autoComplete="off"
                        onChange={(e) => handlePendingChange(tableRowId, "password", e.target.value)}
                    />
                );
            },
        },

        {
            title: "Actions",
            field: "id",
            render: (rowData) => {
                const tableRowId = rowData.tableData!.id;
                const isEditing = !!editingRows[tableRowId];

                if (!isEditing) {
                    return (
                        <Button
                            variant="outlined"
                            color="default"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditClick(tableRowId, rowData)}
                        >
                            Edit
                        </Button>
                    );
                }

                return (
                    <div style={{ display: "flex", gap: "8px" }}>
                        <Button variant="contained" color="primary" onClick={() => handleSaveRow(tableRowId)}>
                            Save
                        </Button>
                        <Button variant="text" color="secondary" onClick={() => handleCancelClick(tableRowId)}>
                            Cancel
                        </Button>
                    </div>
                );
            },
        },

        {
            title: "Status",
            field: "isActive",
            render: (rowData) => {
                const tableRowId = rowData.tableData!.id;

                // Validation rule: Switch is disabled if EITHER property is blank/unset
                const isConfigMissing = !rowData.password || !rowData.frequency;

                return (
                    <GreenSwitch
                        checked={rowData.isActive}
                        color="primary"
                        disabled={isConfigMissing} // Disables the switch element natively
                        onChange={(e) => handleToggleActive(tableRowId, e.target.checked, rowData)}
                    />
                );
            },
        },
    ];

    // Gmail Email Field Logic Handlers
    const isValidEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Add chip if user presses Enter, Comma, or Space
        if (["Enter", ",", " "].includes(e.key)) {
            e.preventDefault();
            const value = emailInput.trim().replace(/,$/, ""); // strip trailing comma if present

            if (!value) return;

            if (!isValidEmail(value)) {
                setEmailError("Please enter a valid email address");
                return;
            }

            if (emailList.includes(value)) {
                setEmailError("This email has already been added");
                return;
            }

            setEmailList([...emailList, value]);
            setEmailInput("");
            setEmailError("");
        }
    };

    const handleEmailPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text");
        // Split by comma, space, or newline to handle bulk pastes
        const emails = pastedData.split(/[\s,\n]+/);

        const newEmails = emails
            .map(email => email.trim())
            .filter(email => email && isValidEmail(email) && !emailList.includes(email));

        if (newEmails.length > 0) {
            setEmailList([...emailList, ...newEmails]);
            setEmailInput("");
            setEmailError("");
        } else {
            setEmailError("No valid or new email addresses found in clipboard");
        }
    };


    const deleteEmailChip = (emailToDelete: string) => {
        setEmailList(emailList.filter(email => email !== emailToDelete));
    };

    return (
        <div style={{ maxWidth: "100%", margin: "20px" }}>
            <IonItem>
                <h3>Schedule Reports</h3>
            </IonItem>

            {/* GMAIL-STYLE RECIPIENT CONTROL BOX POSITIONED DIRECTLY BELOW IONITEM */}
            <div style={{
                margin: "15px 0 25px 0",
                padding: "12px",
                border: "1px solid #fafafa",
                borderRadius: "4px",
                backgroundColor: "#fafafa",
                width: '30%',
                flexDirection: 'row'
            }}>
                <IonItem>
                    <Typography variant='subtitle1' style={{ color: "#666", marginRight: 10, fontWeight: 'bold' }}>
                        Send To:
                    </Typography>
                    <Chip
                        key={userEmail}
                        //label={userEmail}
                        label={userEmail}
                        //onDelete={() => deleteEmailChip(email)}
                        color="primary"
                        variant="outlined"
                        size='medium'
                    />
                </IonItem>

                {/* 
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    //gap: "6px",
                    alignItems: "center",
                    minHeight: "40px",
                    //width: '50%',
                    padding: "4px",
                    backgroundColor: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px"
                }}> */}
                {/* <Chip
                        key={userEmail}
                        label={userEmail}
                        //onDelete={() => deleteEmailChip(email)}
                        color="primary"
                        variant="outlined"
                        size="small"
                    /> */}

                {/* {emailList.map((email) => (
                        <Chip
                            key={email}
                            label={email}
                            onDelete={() => deleteEmailChip(email)}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    ))} */}
                {/* <input
                        value={emailInput}
                        placeholder={emailList.length <= 1 ? "Type an email and press Enter or Comma..." : ""}
                        onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                        onKeyDown={handleEmailKeyDown}
                        onPaste={handleEmailPaste}
                        style={{
                            border: "none",
                            outline: "none",
                            flexGrow: 1,
                            padding: "6px",
                            fontSize: "14px",
                            minWidth: "200px"
                        }}
                    /> */}
            </div>
            {/* {emailError && (
                    <Typography variant="caption" color="error" style={{ display: "block", marginTop: "4px" }}>
                        {emailError}
                    </Typography>
                )} */}
            {/* </div> */}


            <MaterialTable
                title="Report Settings"
                columns={columns}
                data={data}
                options={{ search: false, paging: false }}
                isLoading={isLoading}
            />

            <Dialog open={isDialogOpen} onClose={closeSendDialog} fullWidth maxWidth="xs">
                <DialogTitle>Report Email Confirmation</DialogTitle>
                <DialogContent dividers>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <Typography variant="body1"><strong>Send To:</strong> {userEmail}</Typography>
                        <Typography variant="body1"><strong>Report Name:</strong> {activeDialogRow?.reportName}</Typography>
                        <Typography variant="body1"><strong>Type:</strong> {activeDialogRow?.type}</Typography>
                        <Typography variant="body1"><strong>Frequency:</strong> {activeDialogRow?.frequency}</Typography>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeSendDialog} color="secondary">Cancel</Button>
                    <Button onClick={handleSendReport} color="primary" variant="contained">Send</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={warningDialogOpen}
                onClose={() => {
                    setwarningDialogOpen(false)
                }}
                fullWidth maxWidth="xs">
                <DialogTitle>Report Email Confirmation</DialogTitle>
                <DialogContent dividers>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <Typography variant="body1">Are you sure you want to stop the automated email schedule?</Typography>
                        <Typography variant="body1"><strong>Send To:</strong> {userEmail}</Typography>
                        <Typography variant="body1"><strong>Report Name:</strong> {activeDialogRow?.reportName}</Typography>
                        <Typography variant="body1"><strong>Frequency:</strong> {activeDialogRow?.frequency}</Typography>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDeactivation} color="secondary">Cancel</Button>
                    <Button onClick={() => {
                        deleteReportSchedule()
                    }} color="primary" variant="contained">Yes</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
